import { useCallback, useEffect, useState } from 'react'
import { errorMessage, unwrap } from 'api/call'
import {
  getUsersByIdAdminNotifications,
  getUsersByIdAdminTimeline,
  type UserAdminNotificationsResponse,
  type UserTimelineEvent,
} from 'api/generated'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Skeleton } from 'components/ui/skeleton'
import {
  formatDateTime,
  NOTIFIER_LABELS,
  timelineDescription,
  TIMELINE_KINDS,
} from 'helpers/user-details'
import { ALL } from 'helpers/user-filters'
import { cn } from 'lib/utils'
import { Loader } from 'lucide-react'
import { InfoTile } from './UserDetailsParts'

const ListSkeleton = () => (
  <div className='space-y-2'>
    {Array.from({ length: 5 }, (_, index) => (
      <Skeleton key={index} className='h-14' />
    ))}
  </div>
)

const LoadMore = ({
  loading,
  onClick,
}: {
  loading: boolean
  onClick: () => void
}) => (
  <Button
    variant='outline'
    className='w-full'
    disabled={loading}
    onClick={onClick}
  >
    {loading && <Loader className='animate-spin' />} Carica altri
  </Button>
)

/** Long text closed to two lines, opened on demand. */
const CollapsibleText = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false)
  const long = text.length > 140 || text.includes('\n')
  return (
    <div className='mt-1 rounded-md bg-muted/50 p-2 text-sm'>
      <p
        className={cn(
          'break-words whitespace-pre-line',
          !open && long && 'line-clamp-2'
        )}
      >
        {text}
      </p>
      {long && (
        <button
          type='button'
          onClick={() => setOpen(!open)}
          className='mt-1 text-xs font-medium text-muted-foreground hover:underline'
        >
          {open ? 'Riduci' : 'Mostra tutto'}
        </button>
      )}
    </div>
  )
}

type Notification = UserAdminNotificationsResponse['notifications'][number]

/** Notifications received by the user, 30 at a time, newest first. */
export const NotificationsTab = ({ userId }: { userId: number }) => {
  const [type, setType] = useState(ALL)
  const [counts, setCounts] =
    useState<UserAdminNotificationsResponse['counts']>()
  const [items, setItems] = useState<Notification[] | null>(null)
  const [cursor, setCursor] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPage = useCallback(
    (beforeId?: number) =>
      unwrap(
        getUsersByIdAdminNotifications({
          path: { id: String(userId) },
          query: { before_id: beforeId, type: type === ALL ? undefined : type },
        })
      ),
    [userId, type]
  )

  const apply = (page: UserAdminNotificationsResponse, append: boolean) => {
    setCounts(page.counts)
    setItems((current) =>
      append && current
        ? [...current, ...page.notifications]
        : page.notifications
    )
    setCursor(page.next_cursor)
    setError(null)
  }

  // First page, again when the type filter changes; a late answer for a
  // previous filter is dropped
  useEffect(() => {
    let active = true
    fetchPage()
      .then((page) => active && apply(page, false))
      .catch(
        (e) => active && setError(errorMessage(e, 'Notifiche non caricate.'))
      )
    return () => {
      active = false
    }
  }, [fetchPage])

  const loadMore = async (beforeId: number) => {
    setLoading(true)
    try {
      apply(await fetchPage(beforeId), true)
    } catch (e) {
      setError(errorMessage(e, 'Notifiche non caricate.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='space-y-4'>
      {counts && (
        <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
          <InfoTile label='Ricevute' value={counts.total} />
          <InfoTile label='Da leggere' value={counts.unread} />
          <InfoTile label='Mai viste' value={counts.unseen} />
          <InfoTile label='Cancellate' value={counts.deleted} />
        </div>
      )}

      {counts && Object.keys(counts.by_type).length > 0 && (
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className='w-full sm:w-72'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tutti i tipi ({counts.total})</SelectItem>
            {Object.entries(counts.by_type)
              .sort(([, a], [, b]) => b - a)
              .map(([notifier, count]) => (
                <SelectItem key={notifier} value={notifier}>
                  {NOTIFIER_LABELS[notifier] ?? notifier} ({count})
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )}

      {error && <p className='text-red-500'>{error}</p>}
      {!items ? (
        !error && <ListSkeleton />
      ) : items.length === 0 ? (
        <p className='text-sm text-muted-foreground'>Nessuna notifica.</p>
      ) : (
        <ul className='space-y-2'>
          {items.map((notification) => (
            <li
              key={notification.id}
              className={cn(
                'rounded-lg border p-2 text-sm',
                notification.deleted_at && 'opacity-60'
              )}
            >
              <div className='flex flex-wrap items-center gap-2'>
                <Badge variant='outline'>
                  {NOTIFIER_LABELS[notification.type] ?? notification.kind}
                </Badge>
                {notification.deleted_at ? (
                  <Badge variant='secondary'>Cancellata</Badge>
                ) : notification.read_at ? (
                  <Badge variant='secondary'>Letta</Badge>
                ) : notification.seen_at ? (
                  <Badge variant='secondary'>Vista</Badge>
                ) : (
                  <Badge>Nuova</Badge>
                )}
                <span className='text-xs text-muted-foreground'>
                  {formatDateTime(notification.created_at)}
                </span>
              </div>
              {notification.title && (
                <p className='mt-1 font-medium'>{notification.title}</p>
              )}
              {notification.content && (
                <p className='text-muted-foreground'>{notification.content}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {cursor && (
        <LoadMore loading={loading} onClick={() => loadMore(cursor)} />
      )}
    </div>
  )
}

/** What the user did, newest first, 50 events at a time. */
export const TimelineTab = ({ userId }: { userId: number }) => {
  const [events, setEvents] = useState<UserTimelineEvent[] | null>(null)
  const [before, setBefore] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPage = useCallback(
    (cursor?: string) =>
      unwrap(
        getUsersByIdAdminTimeline({
          path: { id: String(userId) },
          query: { before: cursor },
        })
      ),
    [userId]
  )

  useEffect(() => {
    let active = true
    fetchPage()
      .then((page) => {
        if (!active) return
        setEvents(page.events)
        setBefore(page.next_before)
      })
      .catch(
        (e) => active && setError(errorMessage(e, 'Timeline non caricata.'))
      )
    return () => {
      active = false
    }
  }, [fetchPage])

  const loadMore = async (cursor: string) => {
    setLoading(true)
    try {
      const page = await fetchPage(cursor)
      setEvents((current) => [...(current ?? []), ...page.events])
      setBefore(page.next_before)
      setError(null)
    } catch (e) {
      setError(errorMessage(e, 'Timeline non caricata.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='space-y-4'>
      {error && <p className='text-red-500'>{error}</p>}
      {!events ? (
        !error && <ListSkeleton />
      ) : (
        <ol className='relative space-y-3 border-s ps-4'>
          {events.map((event, index) => {
            const { label, Icon, tone } = TIMELINE_KINDS[event.kind]
            const description = timelineDescription(event)
            return (
              <li
                key={`${index}-${event.kind}-${event.at}`}
                className='relative'
              >
                <span
                  className={cn(
                    'absolute -start-[1.6rem] flex size-6 items-center justify-center rounded-full border bg-background',
                    tone
                  )}
                >
                  <Icon className='size-3.5' />
                </span>
                <div className='flex flex-wrap items-baseline gap-x-2 text-sm'>
                  <span className='font-medium'>{label}</span>
                  <span className='text-xs text-muted-foreground'>
                    {formatDateTime(event.at)}
                  </span>
                </div>
                {description && (
                  <p className='text-sm break-words text-muted-foreground'>
                    {description}
                  </p>
                )}
                {event.kind === 'message_sent' && event.detail && (
                  <CollapsibleText text={event.detail} />
                )}
              </li>
            )
          })}
        </ol>
      )}
      {before && (
        <LoadMore loading={loading} onClick={() => loadMore(before)} />
      )}
    </div>
  )
}
